import { type AgentStats } from "./api";

export interface AgentStatusSignal {
  label: string;
  tone: "emerald" | "amber" | "neutral";
}

export function resolveAgentStatus(
  data: AgentStats | undefined,
  isRunning: boolean,
): AgentStatusSignal {
  if (!data) return { label: "", tone: "neutral" };
  if (isRunning) return { label: "Crawling", tone: "emerald" };
  if (!data.plantId) return { label: "Setup required", tone: "amber" };
  if (data.sources.total === 0) return { label: "No sources", tone: "neutral" };
  return { label: "Operational", tone: "emerald" };
}

export function systemSummary(
  data: AgentStats,
  isRunning: boolean,
  lastCrawlStr: string | null,
): string {
  if (data.sources.total === 0) {
    return "No sources configured — connect your first data source to begin.";
  }

  const parts: string[] = [];
  const paused = data.sources.total - data.sources.enabled;

  if (paused > 0) {
    parts.push(
      `${data.sources.enabled} of ${data.sources.total} sources active · ${paused} paused`,
    );
  } else {
    parts.push(
      `${data.sources.enabled} ${data.sources.enabled === 1 ? "source" : "sources"} active`,
    );
  }

  if (isRunning) {
    parts.push("crawling now");
  } else if (lastCrawlStr) {
    parts.push(`last crawl ${lastCrawlStr}`);
  }

  if (data.artifacts.delivered > 0) {
    parts.push(`${data.artifacts.delivered} artifacts delivered`);
  }

  return parts.join(" · ");
}

export function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const seconds = Math.round((Date.now() - ms) / 1000);
  if (seconds < 0) return null;
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}
